import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { applyDeliveryRegion } from '@theme/bt-delivery-region';

/** Survives Combined Listings / picker morphs within the same page. */
let explicitSizeSelected = false;
let explicitSizeValue = '';
let wishlistPressed = false;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Bo+Tee PDP orchestrator — explicit size, mobile sticky select-size, notify drawer, wishlist.
 *
 * @extends {Component}
 */
class BtProductInformation extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  /** @type {AbortController | null} */
  #stickyScrollAbort = null;

  /** @type {boolean} */
  #primaryInView = false;

  /** @type {boolean} */
  #sizeAreaInView = false;

  /** @type {boolean} */
  #sizeAreaPassed = false;

  /** @type {MediaQueryList | null} */
  #mobileMql = null;

  /** @type {number | undefined} */
  #notifyTimer;

  /** @type {MutationObserver | null} */
  #purpleDotObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#mobileMql = window.matchMedia('(max-width: 989px)');
    this.#mobileMql.addEventListener('change', this.#onViewportChange);

    this.#applySizeSelection();

    this.#applyWishlistState();
    this.#syncActions();
    this.#bindGalleryProgress();
    this.#bindSticky();
    this.#bindPurpleDot();

    this.addEventListener('click', this.#onClick, { signal, capture: true });
    this.addEventListener('change', this.#onChange, { signal, capture: true });
    this.addEventListener('input', this.#onNotifyInput, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener('wishlist:state', this.#onWishlistState, { signal });
    document.addEventListener('bt-pdp:wishlist-state', this.#onWishlistState, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#mobileMql?.removeEventListener('change', this.#onViewportChange);
    this.#mobileMql = null;
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    this.#stickyScrollAbort?.abort();
    this.#stickyScrollAbort = null;
    if (this.#notifyTimer) window.clearTimeout(this.#notifyTimer);
    this.#purpleDotObserver?.disconnect();
    this.#purpleDotObserver = null;
  }

  get #sizeRadios() {
    return Array.from(this.querySelectorAll('[data-bt-pdp-size-group] input[type="radio"]'));
  }

  get #primary() {
    return this.querySelector('[data-bt-pdp-primary]');
  }

  get #sizeArea() {
    return this.querySelector('[data-bt-pdp-size-group]') || this.#primary;
  }

  get #sticky() {
    return this.querySelector('[data-bt-pdp-sticky]');
  }

  get #stickyCta() {
    return this.querySelector('[data-bt-pdp-sticky-cta]');
  }

  get #labels() {
    return {
      selectSize: this.dataset.selectSizeLabel || 'select size',
      addToBag: this.dataset.addToBagLabel || 'add to bag',
      outOfStock: this.dataset.outOfStockLabel || 'out of stock',
      notifyInvalid: this.dataset.notifyInvalid || 'Enter a valid email',
      notifySending: this.dataset.notifySending || 'Sending…',
      notifySuccess: this.dataset.notifySuccess || 'We will email you when this is back',
      notifyError: this.dataset.notifyError || 'Something went wrong. Try again.',
      wishlistAdd: this.dataset.wishlistAdd || 'Add to wishlist',
      wishlistAdded: this.dataset.wishlistAdded || 'Added to wishlist',
    };
  }

  #hasSizeOption() {
    return this.#sizeRadios.length > 0;
  }

  #selectedSizeRadio() {
    return this.#sizeRadios.find((input) => input instanceof HTMLInputElement && input.checked) || null;
  }

  #firstAvailableSizeRadio() {
    return (
      this.#sizeRadios.find(
        (input) => input instanceof HTMLInputElement && input.dataset.optionAvailable !== 'false'
      ) || null
    );
  }

  /**
   * @param {HTMLInputElement | null | undefined} input
   */
  #rememberSize(input) {
    explicitSizeSelected = Boolean(input);
    explicitSizeValue = input?.value || '';
  }

  #isMobileViewport() {
    return this.#mobileMql?.matches ?? window.matchMedia('(max-width: 989px)').matches;
  }

  #onViewportChange = () => {
    this.#bindSticky();
  };

  /**
   * Restore a size the shopper chose (including after a picker morph).
   * One-size products are selected by default. Never preselect out-of-stock
   * when there is more than one size.
   */
  #applySizeSelection() {
    if (explicitSizeSelected && explicitSizeValue) {
      const remembered = this.#sizeRadios.find(
        (input) => input instanceof HTMLInputElement && input.value === explicitSizeValue
      );
      if (remembered instanceof HTMLInputElement) {
        remembered.checked = true;
        this.#rememberSize(remembered);
        this.#paintSelectedChoice(remembered);
        this.#syncActions();
        return;
      }
    }

    const soleSize = this.#soleSizeRadio();
    if (soleSize) {
      soleSize.checked = true;
      this.#rememberSize(soleSize);
      this.#paintSelectedChoice(soleSize);
      this.#syncActions();
      return;
    }

    this.#clearSizeSelection();
    this.#syncActions();
  }

  /** @returns {HTMLInputElement | null} */
  #soleSizeRadio() {
    if (this.#sizeRadios.length !== 1) return null;
    const input = this.#sizeRadios[0];
    return input instanceof HTMLInputElement ? input : null;
  }

  #clearSizeSelection() {
    explicitSizeSelected = false;
    explicitSizeValue = '';
    for (const input of this.#sizeRadios) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = false;
      input.dataset.currentChecked = 'false';
      const choice = input.closest('.bt-pdp-picker__choice');
      choice?.classList.remove('is-selected');
      this.#paintChoiceLabel(choice, false);
    }
  }

  /**
   * @param {Element | null | undefined} choice
   * @param {boolean} selected
   */
  #paintChoiceLabel(choice, selected) {
    const label = choice?.querySelector('.bt-pdp-picker__choice-label');
    if (!label) return;
    const input = choice?.querySelector('input[type="radio"]');
    const unavailable = Boolean(
      choice?.classList.contains('is-unavailable') || input?.dataset.optionAvailable === 'false'
    );
    const bold = Boolean(selected);
    label.classList.toggle('text-p4-bold', bold);
    label.classList.toggle('text-p4', !bold);
    label.classList.toggle('color-text-primary', bold || !unavailable);
    label.classList.toggle('color-text-subdued', unavailable && !bold);
  }

  /**
   * Paint selected/unselected chips immediately so length/size don't wait on
   * the variant-picker section fetch.
   * @param {HTMLInputElement} target
   */
  #paintSelectedChoice(target) {
    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const selected = input === target;
      input.dataset.currentChecked = selected ? 'true' : 'false';
      const choice = input.closest('.bt-pdp-picker__choice, .bt-pdp-picker__swatch');
      choice?.classList.toggle('is-selected', selected);
      this.#paintChoiceLabel(choice, selected);
    }
  }

  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches('[data-bt-pdp-delivery-select]')) {
      this.#onDeliveryRegionChange(target);
      return;
    }

    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    this.#paintSelectedChoice(target);

    if (target.dataset.optionKind === 'size') {
      this.#rememberSize(target);
      if (target.dataset.optionAvailable === 'false') {
        event.stopImmediatePropagation();
        this.#syncActions();
        this.#openNotify();
        return;
      }
    }

    this.#syncActions();
  };

  /**
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const selectEvent = /** @type {ProductSelectEvent} */ (event);
    const connectedUrl = selectEvent.detail?.connectedProductUrl;
    if (connectedUrl && connectedUrl !== this.dataset.productUrl) {
      explicitSizeSelected = false;
      explicitSizeValue = '';
    }

    selectEvent.promise
      ?.then((result) => {
        if (!this.isConnected) return;
        this.#syncPriceAndSave(result?.detail?.html);
        this.#applySizeSelection();
        this.#applyWishlistState();
        this.#bindGalleryProgress();
        this.#bindSticky();
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.#applySizeSelection();
          this.#syncActions();
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bt-pdp] ProductSelectEvent rejected:', error);
      });
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openLook = target.closest('[data-shop-the-look-open]');
    if (openLook instanceof HTMLElement) {
      const lookId = openLook.dataset.lookId;
      if (lookId) {
        event.preventDefault();
        this.#openShopTheLook(lookId, openLook);
      }
      return;
    }

    const trigger = target.closest('[data-bt-pdp-trigger]');
    if (trigger instanceof HTMLElement) {
      const name = trigger.dataset.btPdpTrigger || '';
      if (name === 'size-guide') {
        this.#openSizeGuide();
        this.#dispatchTrigger(name);
        return;
      }
      if (name === 'reviews') {
        this.#openReviews();
        this.#dispatchTrigger(name);
        return;
      }
      this.#openAccordionForTrigger(name);
      this.#dispatchTrigger(name);
      return;
    }

    if (target.closest('[data-bt-pdp-primary]')) {
      if (this.#currentMode() !== 'add') {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      this.#handlePrimaryClick(event);
      return;
    }

    if (target.closest('[data-bt-pdp-sticky-cta]')) {
      this.#handleStickyClick(event);
      return;
    }

    if (target.closest('[data-bt-pdp-notify]')) {
      this.#openNotify();
      return;
    }

    const unavailableSize = target.closest('.bt-pdp-picker__choice--size.is-unavailable');
    if (unavailableSize && !this.#isWaitlist()) {
      const input = unavailableSize.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement && input.checked) {
        this.#openNotify();
      }
      return;
    }

    if (target.closest('[data-bt-pdp-notify-close]')) {
      this.#closeNotify();
      return;
    }

    if (target.closest('[data-bt-pdp-wishlist]')) {
      this.#toggleWishlist(target.closest('[data-bt-pdp-wishlist]'));
    }
  };

  /**
   * @param {Event} event
   */
  #onNotifyInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches('[data-bt-pdp-notify-form] input[type="email"]')) return;

    input.setCustomValidity('');
    input.removeAttribute('aria-invalid');
    input.closest('.bt-newsletter-field')?.classList.remove('bt-newsletter-field--invalid');
    this.#setNotifyStatus('', false);
  };

  /**
   * @param {Event} event
   */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[data-bt-pdp-notify-form]')) {
      event.preventDefault();
      this.#submitNotify(form);
      return;
    }

    if (form.matches('[data-type="add-to-cart-form"]') && this.#currentMode() !== 'add') {
      event.preventDefault();
      if (this.#currentMode() === 'select-size') this.#focusSizePicker();
    }
  };

  /**
   * @param {Event} event
   */
  #handlePrimaryClick(event) {
    const mode = this.#currentMode();
    if (mode === 'select-size') {
      event.preventDefault();
      this.#focusSizePicker();
      return;
    }
    if (mode === 'unavailable') {
      event.preventDefault();
      if (this.dataset.enableNotify === 'true') this.#openNotify();
    }
  }

  /**
   * @param {Event} event
   */
  #handleStickyClick(event) {
    event.preventDefault();
    const mode = this.#currentMode();
    if (mode === 'select-size') {
      this.#focusSizePicker();
      return;
    }
    if (mode === 'unavailable') {
      if (this.dataset.enableNotify === 'true') this.#openNotify();
      return;
    }
    if (mode !== 'add') return;

    const addButton = this.#primary;
    if (!(addButton instanceof HTMLButtonElement) || addButton.disabled) return;
    addButton.dataset.puppet = 'true';
    addButton.click();
  }

  #focusSizePicker() {
    const group = this.querySelector('[data-bt-pdp-size-group]');
    group?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const first = this.#firstAvailableSizeRadio() || this.#sizeRadios[0];
    if (first instanceof HTMLInputElement) first.focus();
  }

  /**
   * Purple Dot waitlist product (tag `purple-dot-live-waitlist`). The PDP
   * renders the real stock state; the app embed's script rewrites the CTA
   * ("Pre-order" / "Sold out") for variants it covers and injects
   * `#pd-waitlist-info` after the button.
   */
  #isWaitlist() {
    return this.hasAttribute('data-purple-dot-waitlist');
  }

  /**
   * True once Purple Dot has replaced our CTA copy. Variants it does not
   * cover keep our "out of stock" label, so they can never look in stock.
   */
  #purpleDotOwnsCta() {
    if (!this.#isWaitlist()) return false;
    const text = this.#primary?.textContent?.trim().toLowerCase() || '';
    if (!text) return false;
    const { selectSize, addToBag, outOfStock } = this.#labels;
    return ![selectSize, addToBag, outOfStock].some((label) => label.toLowerCase() === text);
  }

  /** Out-of-stock waitlist variant that Purple Dot turned into an enabled pre-order CTA. */
  #purpleDotPreorder() {
    const primary = this.#primary;
    return primary instanceof HTMLButtonElement && !primary.disabled && this.#purpleDotOwnsCta();
  }

  #bindPurpleDot() {
    if (!this.#isWaitlist()) return;
    const primary = this.#primary;
    if (!(primary instanceof HTMLButtonElement)) return;

    // Purple Dot rewrites the button text / disabled state asynchronously
    // (same MutationObserver pattern live-theme uses for its sticky CTA).
    this.#purpleDotObserver = new MutationObserver(() => this.#onPurpleDotMutation());
    this.#purpleDotObserver.observe(primary, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  /**
   * Purple Dot changed the CTA. Only form state and the sticky bar follow;
   * the main button is left alone so the two observers never loop.
   */
  #onPurpleDotMutation() {
    const mode = this.#currentMode();
    const variantInput = this.querySelector('product-form-component [ref="variantId"]');
    if (variantInput instanceof HTMLInputElement) variantInput.disabled = mode !== 'add';

    const primary = this.#primary;
    if (primary instanceof HTMLButtonElement) primary.dataset.actionMode = mode;
    const stickyCta = this.#stickyCta;
    if (stickyCta instanceof HTMLButtonElement) stickyCta.dataset.actionMode = mode;
    const sticky = this.#sticky;
    if (sticky instanceof HTMLElement) sticky.dataset.actionMode = mode;
    this.dataset.actionMode = mode;

    this.#mirrorPrimaryToSticky();
  }

  #mirrorPrimaryToSticky() {
    const primary = this.#primary;
    const stickyCta = this.#stickyCta;
    if (!(primary instanceof HTMLButtonElement) || !(stickyCta instanceof HTMLButtonElement)) return;
    if (this.#currentMode() !== 'add') return;

    const label = primary.querySelector('[data-bt-pdp-primary-label]');
    const stickyLabel = stickyCta.querySelector('[data-bt-pdp-sticky-label]');
    if (label && stickyLabel && stickyLabel.textContent !== label.textContent) {
      stickyLabel.textContent = label.textContent;
    }
    if (stickyCta.disabled !== primary.disabled) stickyCta.disabled = primary.disabled;
  }

  #currentMode() {
    if (this.#hasSizeOption() && !explicitSizeSelected) return 'select-size';

    const sizeRadio = this.#selectedSizeRadio();
    const available = sizeRadio
      ? sizeRadio.dataset.optionAvailable !== 'false' && sizeRadio.dataset.variantInStock !== 'false'
      : this.dataset.variantAvailable !== 'false';

    if (available) return 'add';
    return this.#isWaitlist() && this.#purpleDotPreorder() ? 'add' : 'unavailable';
  }

  /**
   * @param {Document | Element | undefined} html
   */
  #syncPriceAndSave(html) {
    if (!html) return;

    const currentPrice = this.querySelector('[data-bt-pdp-price]');
    const newPrice = html.querySelector('[data-bt-pdp-price]');
    if (currentPrice && newPrice) currentPrice.replaceWith(newPrice.cloneNode(true));

    const currentSave = this.querySelector('[data-bt-pdp-save]');
    const newSave = html.querySelector('[data-bt-pdp-save]');
    if (currentSave && newSave) {
      currentSave.replaceWith(newSave.cloneNode(true));
    } else if (currentSave && !newSave) {
      currentSave.remove();
    } else if (!currentSave && newSave) {
      const metaEnd = this.querySelector('[data-bt-pdp-meta-end]');
      (metaEnd || this.querySelector('[data-bt-pdp-meta]'))?.append(newSave.cloneNode(true));
    }
  }

  #syncActions() {
    const mode = this.#currentMode();
    const labels = this.#labels;
    const labelText =
      mode === 'select-size' ? labels.selectSize : mode === 'unavailable' ? labels.outOfStock : labels.addToBag;

    // Purple Dot owns the CTA copy/disabled state on waitlist products once it
    // has rewritten it; writing over it would only make its observer fight back.
    const purpleDotOwnsCta = mode !== 'select-size' && this.#purpleDotOwnsCta();

    const notifyEnabled = this.dataset.enableNotify === 'true';
    const primary = this.#primary;
    if (primary instanceof HTMLButtonElement) {
      primary.dataset.actionMode = mode;
      if (!purpleDotOwnsCta) {
        primary.disabled = mode === 'unavailable' && !notifyEnabled;
        const label = primary.querySelector('[data-bt-pdp-primary-label]');
        if (label) label.textContent = labelText;
      }
    }

    const sizeRadio = this.#selectedSizeRadio();
    const variantInput = this.querySelector('product-form-component [ref="variantId"]');
    if (variantInput instanceof HTMLInputElement) {
      if (sizeRadio?.dataset.variantId) variantInput.value = sizeRadio.dataset.variantId;
      variantInput.disabled = mode !== 'add';
    }

    const notify = this.querySelector('[data-bt-pdp-notify]');
    if (notify instanceof HTMLElement) {
      notify.hidden = mode !== 'unavailable' || this.dataset.enableNotify !== 'true';
    }

    const sticky = this.#sticky;
    const stickyCta = this.#stickyCta;
    if (stickyCta instanceof HTMLButtonElement) {
      stickyCta.dataset.actionMode = mode;
      if (!purpleDotOwnsCta) {
        stickyCta.disabled = mode === 'unavailable' && !notifyEnabled;
        const stickyLabel = stickyCta.querySelector('[data-bt-pdp-sticky-label]');
        if (stickyLabel) stickyLabel.textContent = labelText;
      }
    }
    if (sticky instanceof HTMLElement) {
      sticky.dataset.actionMode = mode;
    }

    this.#updateStickyVisibility();
    this.#syncNotifyPayload();
    this.dataset.actionMode = mode;

    if (purpleDotOwnsCta) this.#mirrorPrimaryToSticky();
  }

  #syncNotifyPayload() {
    const sizeRadio = this.#selectedSizeRadio();
    const size = sizeRadio?.value?.trim() || '';
    const sizeOut = this.querySelector('[data-bt-pdp-notify-size]');
    const sizeWrap = this.querySelector('[data-bt-pdp-notify-size-wrap]');
    if (sizeOut) sizeOut.textContent = size.toLowerCase();
    if (sizeWrap instanceof HTMLElement) sizeWrap.hidden = size === '';

    const priceOut = this.querySelector('[data-bt-pdp-notify-price]');
    if (priceOut) {
      const formatted = sizeRadio?.dataset.variantPrice || this.#notifyPriceFallback();
      if (formatted) priceOut.textContent = formatted;
    }

    const image = this.querySelector('[data-bt-pdp-notify-image]');
    const imageSrc = sizeRadio?.dataset.variantImage;
    if (image instanceof HTMLImageElement && imageSrc) image.src = imageSrc;
  }

  /**
   * @returns {string}
   */
  #notifyPriceFallback() {
    const node = this.querySelector(
      '[data-bt-pdp-price] .bt-pdp-stack__sale, [data-bt-pdp-price] .bt-pdp-stack__price'
    );
    return node?.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  #bindGalleryProgress() {
    const gallery = this.querySelector('media-gallery');
    const scroller = gallery?.querySelector('[data-bt-pdp-gallery-track]');
    const fill = gallery?.querySelector('.bt-pdp-gallery__progress-fill');
    if (!(scroller instanceof HTMLElement) || !(fill instanceof HTMLElement)) return;

    const update = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      const progress = max > 0 ? scroller.scrollLeft / max : 0;
      gallery?.style.setProperty('--bt-pdp-gallery-progress', `${Math.min(1, Math.max(0, progress))}`);
    };

    scroller.addEventListener('scroll', update, { passive: true, signal: this.#abortController?.signal });
    update();
  }

  #bindSticky() {
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    this.#stickyScrollAbort?.abort();
    this.#stickyScrollAbort = null;
    this.#primaryInView = false;
    this.#sizeAreaInView = false;
    this.#sizeAreaPassed = false;

    const sticky = this.#sticky;
    if (!(sticky instanceof HTMLElement)) return;

    if (this.dataset.enableStickySelectSize !== 'true' || !this.#isMobileViewport()) {
      this.#updateStickyVisibility();
      return;
    }

    const primary = this.#primary;
    const sizeArea = this.#sizeArea;
    if (!(sizeArea instanceof HTMLElement)) {
      this.#updateStickyVisibility();
      return;
    }

    this.#stickyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === sizeArea) {
            this.#sizeAreaInView = Boolean(entry.isIntersecting);
            if (entry.boundingClientRect.bottom <= 0) this.#sizeAreaPassed = true;
          }
          if (entry.target === primary) this.#primaryInView = Boolean(entry.isIntersecting);
        }
        this.#updateStickyVisibility();
      },
      { threshold: 0 }
    );
    this.#stickyObserver.observe(sizeArea);
    if (primary instanceof HTMLElement && primary !== sizeArea) {
      this.#stickyObserver.observe(primary);
    }

    this.#stickyScrollAbort = new AbortController();
    window.addEventListener('scroll', this.#onStickyScroll, {
      passive: true,
      signal: this.#stickyScrollAbort.signal,
    });

    this.#readStickyGeometry(sizeArea, primary);
    this.#updateStickyVisibility();
  }

  /**
   * IO only fires on intersection changes. Colour morphs and iOS chrome
   * can move the size block without a new entry.
   */
  #onStickyScroll = () => {
    const sizeArea = this.#sizeArea;
    if (!(sizeArea instanceof HTMLElement)) return;
    this.#readStickyGeometry(sizeArea, this.#primary);
    this.#updateStickyVisibility();
  };

  /**
   * @param {Element} sizeArea
   * @param {Element | null} primary
   */
  #readStickyGeometry(sizeArea, primary) {
    const sizeRect = sizeArea.getBoundingClientRect();
    this.#sizeAreaInView = sizeRect.bottom > 0 && sizeRect.top < window.innerHeight;
    if (sizeRect.bottom <= 0) this.#sizeAreaPassed = true;

    if (primary instanceof HTMLElement) {
      const rect = primary.getBoundingClientRect();
      this.#primaryInView = rect.bottom > 0 && rect.top < window.innerHeight;
    }
  }

  #updateStickyVisibility() {
    const sticky = this.#sticky;
    if (!(sticky instanceof HTMLElement)) return;

    const show =
      this.dataset.enableStickySelectSize === 'true' &&
      this.#isMobileViewport() &&
      this.#sizeAreaPassed &&
      !this.#sizeAreaInView &&
      !this.#primaryInView;

    sticky.hidden = !show;
    sticky.dataset.stuck = show ? 'true' : 'false';
  }

  /**
   * @param {string} name
   */
  #dispatchTrigger(name) {
    if (!name) return;
    this.dispatchEvent(
      new CustomEvent('bt-pdp:trigger', {
        bubbles: true,
        detail: {
          name,
          productId: this.dataset.productId,
          productHandle: this.dataset.productHandle,
        },
      })
    );
  }

  #openSizeGuide() {
    const drawer = this.querySelector('bt-size-guide-drawer');
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  }

  #openReviews() {
    const drawer = this.querySelector('bt-reviews-drawer');
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  }

  /**
   * @param {string} lookId
   * @param {HTMLElement} trigger
   */
  #openShopTheLook(lookId, trigger) {
    const drawer = this.querySelector('shop-the-look-drawer');
    if (drawer && typeof drawer.openLook === 'function') {
      drawer.openLook(lookId, trigger);
      return;
    }

    customElements.whenDefined('shop-the-look-drawer').then(() => {
      const next = this.querySelector('shop-the-look-drawer');
      if (next && typeof next.openLook === 'function') {
        next.openLook(lookId, trigger);
      }
    });
  }

  /**
   * Matching slot names open the inline accordion row.
   * @param {string} name
   */
  #openAccordionForTrigger(name) {
    const slotName = name;
    const item = this.querySelector(`[data-bt-pdp-slot="${slotName}"]`);
    if (!(item instanceof HTMLDetailsElement)) return;
    item.open = true;
    item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * @param {HTMLSelectElement} select
   */
  #onDeliveryRegionChange(select) {
    applyDeliveryRegion(select);
  }

  #openNotify() {
    if (this.dataset.enableNotify !== 'true') return;
    this.#syncNotifyPayload();
    this.#setNotifyStatus('', false);
    const drawer = this.querySelector('bt-notify-drawer');
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
      return;
    }
    const dialog = this.querySelector('[data-bt-pdp-notify-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    dialog.showModal();
    dialog.querySelector('input[type="email"]')?.focus();
  }

  #closeNotify() {
    const drawer = this.querySelector('bt-notify-drawer');
    if (drawer && typeof drawer.closeDialog === 'function') {
      drawer.closeDialog();
      return;
    }
    const dialog = this.querySelector('[data-bt-pdp-notify-dialog]');
    if (dialog instanceof HTMLDialogElement) dialog.close();
  }

  /**
   * @param {HTMLFormElement} form
   */
  #submitNotify(form) {
    const input = form.querySelector('input[type="email"]');
    const email = input instanceof HTMLInputElement ? input.value.trim() : '';
    const submit = form.querySelector('button[type="submit"]');

    if (!EMAIL_PATTERN.test(email)) {
      const message = this.#labels.notifyInvalid;
      if (input instanceof HTMLInputElement) {
        input.setCustomValidity(message);
        input.setAttribute('aria-invalid', 'true');
        input.closest('.bt-newsletter-field')?.classList.add('bt-newsletter-field--invalid');
        input.reportValidity();
        input.focus();
      }
      this.#setNotifyStatus(message, true, 'error');
      return;
    }

    if (input instanceof HTMLInputElement) {
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
      input.closest('.bt-newsletter-field')?.classList.remove('bt-newsletter-field--invalid');
    }
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    this.#setNotifyStatus(this.#labels.notifySending, true, 'loading');

    const sizeRadio = this.#selectedSizeRadio();
    const payload = {
      email,
      productId: this.dataset.productId || '',
      productHandle: this.dataset.productHandle || '',
      variantId: sizeRadio?.dataset.variantId || '',
      colour: this.querySelector('[data-bt-pdp-colour-name]')?.textContent?.trim() || '',
      size: sizeRadio?.value || '',
    };

    let settled = false;
    const finish = (ok, message) => {
      if (settled) return;
      settled = true;
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
      this.#setNotifyStatus(message || (ok ? this.#labels.notifySuccess : this.#labels.notifyError), true, ok ? 'success' : 'error');
    };

    this.dispatchEvent(
      new CustomEvent('bt-pdp:stock-notify', {
        bubbles: true,
        cancelable: true,
        detail: {
          ...payload,
          setResult: (status, message) => finish(status === 'success', message),
        },
      })
    );

    this.#notifyTimer = window.setTimeout(() => {
      if (!settled) finish(true, this.#labels.notifySuccess);
    }, 400);
  }

  /**
   * @param {string} message
   * @param {boolean} visible
   * @param {'error' | 'loading' | 'success' | ''} [kind]
   */
  #setNotifyStatus(message, visible, kind = '') {
    const status = this.querySelector('[data-bt-pdp-notify-status]');
    if (!(status instanceof HTMLElement)) return;
    status.hidden = !visible;
    status.textContent = message;
    status.dataset.state = kind;
  }

  /**
   * @param {Element | null} button
   */
  #toggleWishlist(button) {
    if (!(button instanceof HTMLElement)) return;
    wishlistPressed = button.getAttribute('aria-pressed') !== 'true';
    this.#applyWishlistState();
    this.dispatchEvent(
      new CustomEvent('wishlist:toggle', {
        bubbles: true,
        detail: {
          productId: this.dataset.productId,
          button,
          active: wishlistPressed,
        },
      })
    );
  }

  #applyWishlistState() {
    const button = this.querySelector('[data-bt-pdp-wishlist]');
    if (!(button instanceof HTMLElement)) return;
    button.setAttribute('aria-pressed', wishlistPressed ? 'true' : 'false');
    button.classList.toggle('is-active', wishlistPressed);
    button.setAttribute('aria-label', wishlistPressed ? this.#labels.wishlistAdded : this.#labels.wishlistAdd);
  }

  /**
   * @param {Event} event
   */
  #onWishlistState = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || String(detail.productId) !== String(this.dataset.productId)) return;
    wishlistPressed = Boolean(detail.active);
    this.#applyWishlistState();
  };
}

if (!customElements.get('bt-product-information')) {
  customElements.define('bt-product-information', BtProductInformation);
}

export default BtProductInformation;
